import { CdkPortalOutlet, PortalModule, TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Component,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  signal,
  inject,
} from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LandingService } from '@app/landing/landing.service';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { Culture } from '@app/shared/models/culture.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { NavNotifications } from '@app/shared/models/nav.model';
import { User } from '@app/shared/models/user.model';
import { AppReviewService } from '@app/shared/services/app-review.service';
import { AuthenticationService } from '@app/shared/services/authentication.service';
import { HelperService } from '@app/shared/services/helper.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { NativeService } from '@app/shared/services/native.service';
import { NavService } from '@app/shared/services/nav.service';
import { NotificationsDataService } from '@app/shared/services/notifications.service';
import { UserService } from '@app/shared/services/user.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { lastValueFrom, Observable } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { NgClass, AsyncPipe, UpperCasePipe } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AudioService } from '@app/audio/audio.service';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { VersionRepository } from '@app/shared/state/version/version.repository';
import { AppService } from '@app/app.service';
import { isLegacyPortal } from '@app/shared/utils/legacy-portal';
import { ZendeskFacade } from '@app/shared/components/zendesk-button/zendesk.facade';
import { ZendeskOrigin } from '@app/shared/models/zendesk.model';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-menu-modal',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  standalone: true,
  imports: [
    CdkPortalOutlet,
    BadgeComponent,
    ButtonComponent,
    NgClass,
    AsyncPipe,
    UpperCasePipe,
    TranslateModule,
    PortalModule,
    SharedModule,
    ReactiveFormsModule,
  ],
})
export class MenuModalComponent implements OnInit, AfterViewInit {
  menuModalData = inject<Modal>(MAT_DIALOG_DATA);
  private navService = inject(NavService);
  private versionRepo = inject(VersionRepository);
  private viewContainerRef = inject(ViewContainerRef);
  private helper = inject(HelperService);
  private translateService = inject(TranslateService);
  private userService = inject(UserService);
  logger = inject(LoggerService);
  private audioService = inject(AudioService);
  private authService = inject(AuthenticationService);
  private notificationsDataService = inject(NotificationsDataService);
  private userRepo = inject(UserRepository);
  private eventManager = inject(EventManager);
  private appReviewService = inject(AppReviewService);
  private nativeService = inject(NativeService);
  private landingService = inject(LandingService);
  private dialog = inject(MatDialog);
  private formBuilder = inject(FormBuilder);
  private appService = inject(AppService);
  private zendeskFacade = inject(ZendeskFacade);

  @ViewChildren(CdkPortalOutlet) portalOutlet: QueryList<CdkPortalOutlet>;

  @ViewChild('defaultHeader') defaultHeaderPortalContent: TemplateRef<unknown>;
  @ViewChild('configHeader') configHeaderPortalContent: TemplateRef<unknown>;
  @ViewChild('audioHeader') audioHeaderPortalContent: TemplateRef<unknown>;
  @ViewChild('followUsHeader')
  followUsHeaderPortalContent: TemplateRef<unknown>;
  @ViewChild('aboutUsHeader') aboutUsHeaderPortalContent: TemplateRef<unknown>;

  @ViewChild('facebookFooter')
  facebookFooterPortalContent: TemplateRef<unknown>;
  @ViewChild('configFooter') configFooterPortalContent: TemplateRef<unknown>;

  @ViewChild('main') mainPortalContent: TemplateRef<unknown>;
  @ViewChild('config') configPortalContent: TemplateRef<unknown>;
  @ViewChild('languageSelector')
  languageSelectorPortalContent: TemplateRef<unknown>;
  @ViewChild('audio') audioPortalContent: TemplateRef<unknown>;
  @ViewChild('followUs') followUsPortalContent: TemplateRef<unknown>;
  @ViewChild('aboutUs') aboutUsPortalContent: TemplateRef<unknown>;

  componentDestroy: () => Observable<unknown>;

  headerPortalOutlet: CdkPortalOutlet;
  mainPortalOutlet: CdkPortalOutlet;
  footerPortalOutlet: CdkPortalOutlet;

  currentTemplate: TemplateRef<unknown>;

  showBack = signal(false);
  gameIcon: object;
  cultures: Observable<Culture[]>;
  cultureSelected: string;

  notifications: NavNotifications;
  hasPendingFriends: boolean;

  audioForm: FormGroup;

  isMigrated: boolean;

  get user(): User {
    return this.userRepo.getUser();
  }

  get isNative() {
    return this.nativeService.isNative();
  }

  get version() {
    return this.versionRepo.getFrontendVersion();
  }

  get nativeVersion() {
    return this.nativeService._nativeInfo.version;
  }

  get currentCulture(): string {
    return this.translateService.currentLang;
  }

  ngOnInit(): void {
    this.cultures = this.helper.getSupportedCultures();
    this.cultureSelected = this.currentCulture;
    this.notifications = this.notificationsDataService.notifications;
    this.initAudioSettings();

    this.isMigrated = !isLegacyPortal();
  }

  ngAfterViewInit() {
    this.headerPortalOutlet = this.portalOutlet.toArray()[0];
    this.mainPortalOutlet = this.portalOutlet.toArray()[1];
    this.footerPortalOutlet = this.portalOutlet.toArray()[2];

    this.headerPortalOutlet.attach(this.setTemplatePortal(this.defaultHeaderPortalContent));
    this.mainPortalOutlet.attach(this.setTemplatePortal(this.mainPortalContent));
    this.footerPortalOutlet.attach(this.setTemplatePortal(this.facebookFooterPortalContent));
  }

  changeView(portal: TemplateRef<unknown>): void {
    this.currentTemplate = portal;
    this.mainPortalOutlet.detach();
    this.switchHeaderPortal(portal);
    this.switchFooterPortal(portal);
    this.mainPortalOutlet.attachTemplatePortal(this.setTemplatePortal(portal));
    this.showBack.set(true);
  }

  manageBack(): void {
    if (
      this.currentTemplate === this.languageSelectorPortalContent ||
      this.currentTemplate === this.audioPortalContent
    ) {
      this.changeView(this.configPortalContent);
    } else {
      this.changeView(this.mainPortalContent);
      this.showBack.set(false);
    }
  }

  changeLang(culture: string) {
    this.closeModal();

    this.dialog
      .getDialogById(NonPersistedModalName.MenuModalComponent)
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
        this.setUserCulture(culture);
      });
  }

  goToProfile(): void {
    this.closeModal();

    this.dialog
      .getDialogById(NonPersistedModalName.MenuModalComponent)
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
        this.landingService.showProfile();
      });
  }

  goToContact(): void {
    this.zendeskFacade.goToContact(ZendeskOrigin.InApp);
  }

  goToRateUs(): Promise<void> {
    return this.appReviewService.startReview();
  }

  goToFacebook(): void {
    window.open('https://www.facebook.com/@slotcom/', '_blank');
  }

  goToInstagram(): void {
    window.open('https://www.instagram.com/slotcom/', '_blank');
  }

  goToYoutube(): void {
    window.open('https://www.youtube.com/@SlotcomGames', '_blank');
  }

  openPrivacy(): void {
    window.open(this.translateService.instant('home.lobby.modals.update-tos.privacy-link'), '_blank');
  }

  openTerms(): void {
    window.open(this.translateService.instant('home.lobby.modals.update-tos.terms-link'), '_blank');
  }

  openCookies(): void {
    this.eventManager.openModal(new Modal(NonPersistedModalName.CookiesComponent, ModalType.View));
  }

  copyIdToClipBoard(userId: string) {
    navigator.clipboard.writeText(userId.toString()).catch((err) => console.error('Error on copy', err));
  }

  async logOut(): Promise<void> {
    this.closeModal();

    await lastValueFrom(
      this.dialog.getDialogById(NonPersistedModalName.MenuModalComponent).afterClosed().pipe(first())
    );

    await this.authService.logout();
  }

  closeModal(): void {
    this.navService.closeMenu();
  }

  private setTemplatePortal(portal: TemplateRef<unknown>): TemplatePortal<unknown> {
    return new TemplatePortal(portal, this.viewContainerRef);
  }

  private switchHeaderPortal(portal: TemplateRef<unknown>): void {
    this.headerPortalOutlet.detach();

    switch (portal) {
      case this.mainPortalContent:
        this.headerPortalOutlet.attach(this.setTemplatePortal(this.defaultHeaderPortalContent));
        break;

      case this.configPortalContent:
      case this.languageSelectorPortalContent:
        this.headerPortalOutlet.attach(this.setTemplatePortal(this.configHeaderPortalContent));
        break;
      case this.audioPortalContent:
        this.headerPortalOutlet.attach(this.setTemplatePortal(this.audioHeaderPortalContent));
        break;
      case this.followUsPortalContent:
        this.headerPortalOutlet.attach(this.setTemplatePortal(this.followUsHeaderPortalContent));
        break;

      case this.aboutUsPortalContent:
        this.headerPortalOutlet.attach(this.setTemplatePortal(this.aboutUsHeaderPortalContent));
        break;

      default:
        this.headerPortalOutlet.attach(this.setTemplatePortal(this.defaultHeaderPortalContent));
        break;
    }
  }

  private switchFooterPortal(portal: TemplateRef<unknown>): void {
    this.footerPortalOutlet.detach();

    if (portal === this.mainPortalContent) {
      this.footerPortalOutlet.attach(this.setTemplatePortal(this.facebookFooterPortalContent));
    } else if (portal === this.configPortalContent) {
      this.footerPortalOutlet.attach(this.setTemplatePortal(this.configFooterPortalContent));
    } else {
      this.footerPortalOutlet.detach();
    }
  }

  private async setUserCulture(culture: string): Promise<void> {
    await lastValueFrom(this.userService.updateUserCulture(culture));
    await Promise.resolve(this.userRepo.updateUser({ culture }));
    this.appService.reload();
  }

  private initAudioSettings() {
    this.audioForm = this.formBuilder.group({
      bgm: [!this.audioService.isTypeMuted('BGM')],
      sfx: [!this.audioService.isTypeMuted('SFX')],
    });

    this.audioForm
      .get('bgm')
      ?.valueChanges.pipe(takeUntil(this.componentDestroy()))
      .subscribe((enabled) => this.audioService.setMuteForType('BGM', !enabled));

    this.audioForm
      .get('sfx')
      ?.valueChanges.pipe(takeUntil(this.componentDestroy()))
      .subscribe((enabled) => this.audioService.setMuteForType('SFX', !enabled));

    this.audioService.typeMuteChange$.pipe(takeUntil(this.componentDestroy())).subscribe(({ type, muted }) => {
      const control = this.audioForm.get(type.toLowerCase());
      if (control?.value === !muted) return;
      control?.setValue(!muted, { emitEvent: false });
    });

    this.audioService.globalMute$.pipe(takeUntil(this.componentDestroy())).subscribe((isMuted) => {
      if (isMuted) this.audioForm.disable({ emitEvent: false });
      else this.audioForm.enable({ emitEvent: false });
    });
  }
}
